import React from 'react'
import { JobListing } from '~/lib/sanity.queries'
import { GetStaticProps, GetStaticPaths } from 'next'
import { getClient } from '~/lib/sanity.client'
import { groq } from 'next-sanity'
import { ArrowLeft, MapPin, DollarSign, Shield } from 'lucide-react'
import Link from 'next/link'
import Head from 'next/head'
import Layout from '~/components/Layout'
import { readToken } from '~/lib/sanity.api'
import { getNavigation, Navigation } from '~/lib/sanity.queries'
import { PAGE_TITLE_PREFIX } from '~/components/constants'
import { motion } from 'framer-motion'
import Card from '~/components/Card'
import Button from '~/components/Button'
import PortableTextScrollAnimation from '~/components/TextAnimation'

interface Props {
  jobListing: JobListing
  navigation: Navigation
  draftMode: boolean
  token: string
}

const SingleCareerPage = ({ jobListing, navigation }: Props) => {
  if (!jobListing) return null

  const pageTitle = `${PAGE_TITLE_PREFIX} ${jobListing.title} | Careers`
  const pageDescription = jobListing.description.slice(0, 157) + '...'

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Head>

      <Layout navigation={navigation}>
        <div className="min-h-screen pt-36 md:pt-44">
          <div className="container mx-auto px-4">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                href="/careers"
                className="inline-flex items-center text-white hover:text-primary-700 mb-8"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to All Positions
              </Link>
            </motion.div>

            {/* Job Header */}
            <motion.div
              className="bg-grey-800 rounded-xl p-8 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="h2 text-gradient mb-4">{jobListing.title}</h1>

              <div className="flex flex-wrap gap-4 text-grey">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-green" />
                  <span className="text-white capitalize">
                    {jobListing.department}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-green" />
                  <span className="text-white capitalize">
                    {jobListing.location}
                  </span>
                </div>
                {jobListing.type && (
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-green" />
                    <span className="text-white capitalize">
                      {jobListing.type}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Job Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div
                className="lg:col-span-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Main Content */}
                <div className="bg-grey-800 rounded-xl p-8 space-y-8">
                  {/* Summary */}
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-4">
                      Overview
                    </h2>
                    <div className="prose prose-invert max-w-none">
                      <div className="mt-8 body-large md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto">
                        <PortableTextScrollAnimation
                          value={jobListing.description}
                        />
                      </div>
                    </div>
                  </section>

                  {/* Responsibilities */}
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-4">
                      Responsibilities
                    </h2>
                    <div className="prose prose-invert max-w-none">
                      <ul className="pl-0 space-y-4">
                        {jobListing.responsibilities.map(
                          (responsibility, index) => (
                            <li
                              key={index}
                              className="pl-6 relative before:absolute before:left-0 before:top-[0.6em] before:h-4 before:w-4 before:rounded-full before:bg-gradient md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto"
                            >
                              {responsibility}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </section>

                  {/* Requirements */}
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-4">
                      The Requirements
                    </h2>
                    <div className="prose prose-invert max-w-none">
                      <ul className="pl-0 space-y-4">
                        {jobListing.requirements.map((requirement, index) => (
                          <li
                            key={index}
                            className="pl-6 relative before:absolute before:left-0 before:top-[0.6em] before:h-4 before:w-4 before:rounded-full before:bg-gradient body md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto"
                          >
                            {requirement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  {/* Skills */}
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-4">
                      Valued Skills
                    </h2>
                    <div className="prose prose-invert max-w-none">
                      <ul className="pl-0 space-y-4">
                        {jobListing.skills.map((skill, index) => (
                          <li
                            key={index}
                            className="pl-6 relative before:absolute before:left-0 before:top-[0.6em] before:h-4 before:w-4 before:rounded-full before:bg-gradient body md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto"
                          >
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  {/* Benefits */}
                  {jobListing.benefits && (
                    <section>
                      <h2 className="text-xl font-semibold text-white mb-4">
                        Benefits
                      </h2>
                      <div className="prose prose-invert max-w-none">
                        <ul className="pl-0 space-y-4">
                          {jobListing.benefits.map((benefit, index) => (
                            <li
                              key={index}
                              className="pl-6 relative before:absolute before:left-0 before:top-[0.6em] before:h-4 before:w-4 before:rounded-full before:bg-gradient body md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto"
                            >
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </section>
                  )}
                </div>
              </motion.div>

              {/* Application Sidebar */}
              <motion.div
                className="lg:col-span-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="sticky top-36">
                  <div className="bg-grey-800 rounded-xl text-center">
                    <h2 className="h5 text-white mb-4">
                      Apply for this position
                    </h2>
                    <p className="text-white mb-6">
                      Ready to join our team? Submit your application and
                      we&apos;ll get back to you soon.
                    </p>
                    <Button
                      href={`https://twitter.com/NodeAIETH`}
                      className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Apply Now
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const query = groq`*[_type == "jobListing"] { slug }`
  const jobs = await getClient().fetch(query)

  const paths = jobs.map((job: JobListing) => ({
    params: { slug: job.slug.current },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const [jobListing, navigation] = await Promise.all([
    client.fetch(
      groq`*[_type == "jobListing" && slug.current == $slug][0]{
        _id,
        title,
        slug,
        location,
        type,
        salary,
        responsibilities,
        requirements,
        benefits,
        applicationUrl,
        applicationEmail,
        skills,
        description,
        department
      }`,
      { slug: params?.slug },
    ),
    getNavigation(client),
  ])

  if (!jobListing) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      jobListing,
      navigation,
      draftMode,
      token: draftMode ? readToken : '',
    },
    revalidate: 60, // Revalidate every minute
  }
}

export default SingleCareerPage
